import { produce } from 'immer';
import { OrganisationSettingsHttpService } from 'shared/util-common';

import {
  ChapterWidget,
  ComponentWidget,
  ConnectedGuidelineInformation,
  ConnectedGuidelineInformationDto,
  FlowchartNodeDto,
  Guideline,
  GuidelineChapterType,
  GuidelineDto,
  GuidelineWidget,
  Organisation,
  OrganisationDto,
  OriginalGuidelineInformation,
  OriginalGuidelineInformationDto,
  Role,
  TextWidget,
  User,
  UserDto,
  WidgetType,
} from '@treetop/shared/model';

import { WidgetMap } from '../guideline-graph.service';

export type ChapterConfiguration = {
  widgets?: {
    type: WidgetType;
    data?: object;
  }[];
};

export const ChapterConfigurations: Record<GuidelineChapterType, ChapterConfiguration> = {
  [GuidelineChapterType.Text]: {
    widgets: [{ type: 'text' }],
  },
  [GuidelineChapterType.Conditions]: {
    widgets: [{ type: 'text' }, { type: 'component-list' }, { type: 'text' }],
  },
  [GuidelineChapterType.DiagnosisPlan]: {
    widgets: [{ type: 'text' }, { type: 'component-list' }, { type: 'text' }],
  },
  [GuidelineChapterType.Criteria]: {
    widgets: [{ type: 'text' }, { type: 'component-list' }, { type: 'text' }],
  },
  [GuidelineChapterType.Flowchart]: {
    widgets: [{ type: 'text' }, { type: 'flowchart' }, { type: 'text' }],
  },
  [GuidelineChapterType.Workflow]: {
    widgets: [{ type: 'text' }, { type: 'workflow' }, { type: 'text' }],
  },
};

export class GuidelineServiceUtil {
  /**
   * Returns all children of a widget recursively
   * @param uniqueId The uniqueId of the widget to get the children for
   * @param widgetMap The widget map of the guideline
   * @returns An array of all children widgets
   */
  public static getAllChildrenWidgets(uniqueId: string, widgetMap: WidgetMap) {
    const result: GuidelineWidget[] = [];

    const collectChildren = (id: string): void => {
      const widget = widgetMap[id]();
      if (!widget) {
        return;
      }

      result.push(widget);

      if (widget.widgetType !== 'chapter' && widget.widgetType !== 'component') {
        return;
      }

      const childrenIds = widget.childrenIds ?? [];
      for (const childId of childrenIds) {
        collectChildren(childId);
      }
    };

    collectChildren(uniqueId);

    return result;
  }
}

export const findFlowchartElementRecursive = (elements: FlowchartNodeDto[], uniqueId: string): FlowchartNodeDto | undefined => {
  for (const element of elements) {
    if (element.uniqueId === uniqueId || element.id === uniqueId) return element;

    if (element.children?.length > 0) {
      const found = findFlowchartElementRecursive(element.children, uniqueId);
      if (found) return found;
    }
  }
  return undefined;
};

export const deepMerge = <T>(target: T, source: Partial<T>): T => {
  if (typeof target !== 'object' || target === null) return source as T;
  if (typeof source !== 'object' || source === null) return target;

  const result: any = Array.isArray(target) ? [...target] : { ...target };

  for (const key of Object.keys(source) as (keyof T)[]) {
    const srcVal = source[key];
    const tgtVal = (target as any)[key];

    if (Array.isArray(srcVal)) {
      result[key] = srcVal;
    } else if (typeof srcVal === 'object' && srcVal !== null) {
      result[key] = deepMerge(tgtVal, srcVal);
    } else {
      result[key] = srcVal; // allow undefined overwrite
    }
  }

  return result;
};

function mapOrganisationDto(dto?: OrganisationDto): Organisation | undefined {
  if (!dto) return undefined;
  return {
    ...dto,
    organisationSettings: OrganisationSettingsHttpService.mapOrganisationSettingsDtoToOrganisationSettings(dto.organisationSettings),
  };
}

function mapUserDto(dto?: UserDto): User | undefined {
  if (!dto) return undefined;
  return {
    ...dto,
    currentOrganisation: mapOrganisationDto(dto.currentOrganisation),
    currentRootOrganisation: mapOrganisationDto(dto.currentRootOrganisation),
    homeOrganisation: mapOrganisationDto(dto.homeOrganisation),
    currentRoles: dto.currentRoles as Role[],
    organisations: dto.organisations?.map((org) => mapOrganisationDto(org)).filter((org): org is Organisation => !!org),
  };
}

function mapOriginalGuidelineInformationDto(dto?: OriginalGuidelineInformationDto): OriginalGuidelineInformation | undefined {
  if (!dto) return undefined;
  return {
    ...dto,
    authors: dto.authors?.map(mapUserDto).filter((author): author is User => !!author) ?? [],
    owner: mapUserDto(dto.owner),
    publisher: mapUserDto(dto.publisher),
    publishedAt: dto.publishedAt,
    version: dto.version,
    uniqueId: dto.uniqueId,
  };
}

function mapConnectedGuidelineInformationDto(dto?: ConnectedGuidelineInformationDto): ConnectedGuidelineInformation | undefined {
  if (!dto) return undefined;
  return {
    originalGuidelineInformation: mapOriginalGuidelineInformationDto(dto.originalGuidelineInformation),
    automaticUpdates: dto.automaticUpdates,
    forwardEmailNotifications: dto.forwardEmailNotifications,
    allowLocalChanges: dto.allowLocalChanges,
    allowDetachGuideline: dto.allowDetachGuideline,
    importDate: dto.importDate,
    importer: mapUserDto(dto.importer),
  };
}

export const mapDtoToGuideline = (dto: GuidelineDto): Guideline | undefined => {
  if (!dto) {
    return undefined;
  }

  const updatedDto = produce(dto, (draft) => {
    const widgetMap = draft.widgetMap;
    if (!widgetMap) return;

    Object.values(widgetMap).forEach((parent) => {
      const childIds = [
        ...('childrenIds' in parent && parent.childrenIds ? parent.childrenIds : []),
        ...('widgetIds' in parent && parent.widgetIds ? parent.widgetIds : []),
      ];

      childIds.forEach((childId) => {
        const child = widgetMap[childId];
        if (!child) return;

        widgetMap[childId] = {
          ...child,
          parentId: parent.uniqueId,
        };
      });
    });
  });

  return {
    ...updatedDto,
    creator: mapUserDto(dto.creator),
    editor: mapUserDto(dto.editor),
    setInReviewBy: mapUserDto(dto.setInReviewBy),
    approvedBy: mapUserDto(dto.approvedBy),
    owner: mapUserDto(dto.owner),
    publisher: mapUserDto(dto.publisher),
    deactivatedBy: mapUserDto(dto.deactivatedBy),
    authors: dto.authors?.map((author) => mapUserDto(author)).filter((author): author is User => !!author),
    organisation: mapOrganisationDto(dto.organisation),
    providerOrganisation: mapOrganisationDto(dto.providerOrganisation),
    connectedGuidelineInformation: mapConnectedGuidelineInformationDto(dto.connectedGuidelineInformation),
  };
};

export const mapGuidelineToDtoForSave = (guideline: Guideline): Guideline => {
  if (!guideline.widgetMap) {
    return guideline;
  }

  return guideline;
};

export const sanitizeWidget = (widget: GuidelineWidget): GuidelineWidget => {
  if (widget.widgetType === 'text') {
    return sanitizeTextWidget(widget as TextWidget);
  }
  return widget;
};

const sanitizeTextWidget = (widget: TextWidget): TextWidget => {
  if (!widget.text) {
    return widget;
  }

  // remove all mark tags but keep the inner text
  const sanitizedText = widget.text.replace(/<mark[^>]*>(.*?)<\/mark>/g, '$1');

  return {
    ...widget,
    text: sanitizedText,
  };
};

export const getWidgetsChildren = <T extends ChapterWidget | ComponentWidget>(
  widgets: T[],
  widgetMap: Record<string, GuidelineWidget>,
): T[] => {
  const result: T[] = [];

  for (const widget of widgets) {
    result.push(widget);

    const children = getWidgetChildren<T>(widget, widgetMap);

    result.push(...children);
  }

  return result;
};

export const getWidgetChildren = <T extends ChapterWidget | ComponentWidget>(
  widget: T,
  widgetMap: Record<string, GuidelineWidget>,
): T[] => {
  const widgetsInOrder: T[] = [];

  const collectWidgets = (id: string): void => {
    const widget = widgetMap[id] as T;
    if (!widget) return;

    widgetsInOrder.push(widget);

    const children = widget.childrenIds ?? [];
    for (const childId of children) {
      collectWidgets(childId);
    }
  };

  const children = widget.childrenIds ?? [];
  for (const childId of children) {
    collectWidgets(childId);
  }

  return widgetsInOrder;
};
